<template>
	<div class="page container">
		<v2-back-btn :route="backButtonLink" />
		<div class="deal-pack" v-if="dealPack">
			<div class="deal-pack__header content">
				<div class="main">
					<div class="deal-pack__header--info">
						<div class="tag">
							{{ getDeals.length || '' }} Deals
						</div>
						<div class="tag">
							<icon icon="v2-shield" colour="transparent" />
							{{ getLevel }}
						</div>
						<template v-if="dealPack.biddingSystems && dealPack.biddingSystems.length">
							<tooltip
								v-for="tag in dealPack.biddingSystems"
								:key="tag"
								text="Bidding system for this deal pack"
								position="top"
							>
								<div class="tag">
									{{ tag }}
								</div>
							</tooltip>
						</template>
						<tooltip
							v-else
							text="Bidding system for this deal pack"
							position="top"
						>
							<div class="tag">
								Any
							</div>
						</tooltip>
					</div>
					<div class="deal-pack__header--title">
						{{ dealPack.title }}
					</div>
					<div class="deal-pack__header--subtitle">
						{{ dealPack.description }}
					</div>

					<div class="deal-pack__header--description">
						<div data-element="introduction" v-if="dealPack.introduction">
							<div data-element="introduction" v-html="dealPack.introduction" />
						</div>
					</div>

					<div class="deal-pack__header--controls">
						<V2Button v-if="getShowBuyBtn" :disabled="dealPack.activated" @click="buyDealPack">
							{{ dealPack.activated ? 'Purchased' : 'Buy Deal Pack' }}
						</V2Button>
						<template v-if="!dealPack.activated">
							<span
								class="deal-pack__header--controls__price"
								:class="{ discount: dealPack.costWithDiscount, admin: !getShowBuyBtn && !dealPack.costWithDiscount }"
							>
								{{ getCostString }}
							</span>
							<span
								v-if="dealPack.costWithDiscount"
								class="deal-pack__header--controls__price"
								:class="{ admin: !getShowBuyBtn }"
							>{{ getDiscountString }}</span
							>
						</template>
						<v2-deal-pack-progress v-else :value="dealPack.activityProgress || 0" />
						<V2Button
							v-if="getShowContinueBtn"
							variant="red"
							class="continue-btn"
							@click="continuePlay">
							Continue
						</V2Button>
					</div>
				</div>
				<div class="side">
					<img class="deal-pack__header--img" :src="getDealPackImageUrl" />
				</div>
			</div>
			<div class="separator" />
			<div class="deal-pack__body content">
				<div class="main">
					<div class="deal-pack__body--title">
						Deals
					</div>
					<div class="deal-pack__body--deals">
						<pack-deals-list :deal-pack="dealPack">
							<div class="create-deal-pack" v-if="getContext === 'teaching'">
								Add new deals to see them here
								<V2Button variant="outline" :to="`/teaching/deal-packs/${dealPack.id}/deals`">
									Add deals
								</V2Button>
							</div>
						</pack-deals-list>
					</div>
					<deal-pack-action-groups
						v-if="getDealPackIsLoaded"
						:dealPack="dealPack"
					/>
				</div>
				<div class="side">
					<div class="deal-pack__body--info">
						<div class="deal-pack__body--info-title">
							<icon icon="v2-bookmark" colour="transparent" />
							Key Topics We’ll Cover
						</div>

						<div class="deal-pack__body--info-labels">
							<div class="tag" v-for="tag in dealPack.keywords" :key="tag">
								{{ tag }}
							</div>
						</div>

						<div class="deal-pack__body--info-title">
							<icon icon="v2-teacher" colour="transparent" />
							Your Instructor
						</div>

						<div class="deal-pack__body--info-teacher teacher" @click="openTeacher">
							<img class="teacher__avatar" :src="getImageSrc" />
							<div class="teacher__text">
								<div class="teacher__text--title">
									{{ dealPack.teacherName }}
								</div>
								<div class="teacher__text--subtitle">
									Teacher
								</div>
							</div>
						</div>

						<div v-if="isEditMode" class="deal-pack__body--info-title">
							<icon icon="v2-settings" colour="transparent" />
							Edit
						</div>

						<div v-if="isEditMode" class="deal-pack__body--info-controlls">
							<deal-pack-action-list
								v-if="getDealPackIsLoaded"
								:dealPack="dealPack"
							/>
						</div>

					</div>
				</div>
			</div>

		</div>
	</div>
</template>

<script>
	import Icon                  from '@/components/ui/Icon';
	import DealPackActionGroups  from '@/components/dealPacks/DealPackActionGroups.vue';
	import DealPackActionList    from '@/components/dealPacks/DealPackActionList';
	import V2DealPackProgress    from '@/components/dealPacks/V2DealPackProgress';
	import V2BackBtn             from '@/components/v2/ui/V2BackBtn';
	import V2Button              from '@/components/v2/ui/V2Button';
	import MockImage             from '@/assets/icons/v2/image-placeholder-small.svg';
	import PackDealsList         from '@/components/dealPacks/PackDealsList.vue';
	import Tooltip               from '@/components/ui/Tooltip';
	import actionClick           from '@/mixins/actionClick';
	import routeParams           from '@/mixins/routeParams';
	import api                   from '@/services/api';
	import getStripe             from '@/services/stripe';
	import { dealPackCost, dealPackDiscountCost } from '@/formatters';
	import { SIMULATOR_MODES } from '@/consts';

	export default {
		components: {
			PackDealsList,
			DealPackActionList,
			V2DealPackProgress,
			DealPackActionGroups,
			Tooltip,
			Icon,
			V2BackBtn,
			V2Button
		},
		mixins: [actionClick, routeParams],
		props: {
			dealPack: {
				type: Object,
				default: () => ({})
			}
		},

		computed: {
			backButtonLink () {
        if (this.getContext === 'myDeals') {
          return '/my-deals';
        }

				return `/${this.getContextSlug}/deal-packs`;
			},
			isEditMode () {
				return this.getContext === 'teaching' || this.getContext === 'admin';
			},
			isAdminMode () {
				return this.getContext === 'admin';
			},
			getDiscountString () {
				return dealPackDiscountCost(this.dealPack);
			},
			getLevel () {
        if (this.dealPack.minExperienceLevel === this.dealPack.maxExperienceLevel) {
          return this.dealPack.minExperienceLevel;
        }

        return `${this.dealPack.minExperienceLevel}-${this.dealPack.maxExperienceLevel}`;
			},
			getTeacherName () {
				return this.dealPack?.teacherName;
			},
			getDealPackTitle () {
				return this.dealPack?.title;
			},
			getDescription () {
				return this.dealPack?.description;
			},
			getIntroduction () {
				return this.dealPack?.introduction;
			},
			getCostString () {
				if (this.getIsFree) {
					return 'free';
				}
				if (this.dealPack?.costWithDiscount) {
					return dealPackDiscountCost(this.dealPack);
				}
				return dealPackCost(this.dealPack);
			},
			getIsFree () {
				return this.dealPack.isFree;
			},
			getShowBuyBtnText () {
				if (this.getIsFree) {
					return 'Get for free';
				}
				return `Buy now for ${this.getCostString}`;
			},
			getShowBuyBtn () {
				return this.getContext === 'dealStore';
			},
			getDealPackIsLoaded () {
				return this.dealPack?.id !== undefined;
			},
			getTeacherProfileId () {
				return this.dealPack?.teacherProfileId;
			},
			getTeacherProfileRoute () {
				if (!this.getTeacherProfileId) {
					return '';
				}
				return `/teachers/${this.getTeacherProfileId}`;
			},
			getDeals () {
				return this.dealPack?.deals || [];
			},
			getCompletedDeals () {
				return this.getDeals?.filter((deal) => {
					return deal?.isComplete;
				});
			},
			getNotCompletedDeals () {
				return this.getDeals?.filter((deal) => {
					return !deal?.isComplete;
				});
			},
			getShowContinueBtn () {
				if (this.isEditMode) {
					return false;
				}
				if (this.getDeals.length) {
					return this.getCompletedDeals.length > 0
						? this.getNotCompletedDeals.length > 0
						: false;
				}
				return false;
			},
			getDealPackImageUrl () {
        if (this.dealPack?.media?.[0]?.url) {
          return this.dealPack.media[0].url;
        }

				const focusTypes = {
					bidding: 'Bidding',
					'declarer play': 'Play',
					defence: 'Defence'
				};

				const focus = focusTypes[this.dealPack?.focus?.[0]] || 'Other';

				return `/card-images/${focus}_Lev${this.dealPack.maxExperienceLevel}.png`;
			},
			getImageSrc () {
				if (!this.dealPack?.teacherProfile?.media?.[0]?.url) {
					return MockImage;
				}
				return this.dealPack.teacherProfile.media[0].url;
			}
		},
		mounted () {
		},
		methods: {
			async buyDealPack () {
				const dealPack = await api.dealPacks.buyDealPackById({
					dealPackId: this.dealPack.id
				});
				if (!dealPack) {
					return false;
				}
				if (!dealPack.checkout) {
					// no fee required, no stripe checkout
					this.$router.push(`/my-deals/${this.dealPack.id}`);
					return;
				}
				const stripe = await getStripe();
				stripe.redirectToCheckout({
					sessionId: dealPack.checkout
				});
			},
			openTeacher () {
				this.$router.push(`/teachers/${this.dealPack.teacherProfileId}`);
			},
			async continuePlay () {
				if (this.getNotCompletedDeals.length) {
					const gameMode = this.$store.getters['gameEngine/getGameMode']({ legacyMode: SIMULATOR_MODES.STUDY });
					const roomToken = await this.$store.dispatch('gameEngine/getRoomToken', {
						mode: gameMode,
						context: 'deal_pack',
						dealIds: this.getNotCompletedDeals.map(item => item.id),
						exitRedirectUrl: window.location.href,
						dealRecordingEnabled: [],
						metadata: {
							dealPackId: this.dealPack?.id
						}
					});
					await this.$store.dispatch('gameEngine/openGameEngine', { roomToken, newTab: false });
				}
			}
		}
	};

</script>

<style lang="scss" scoped>
.deal-pack {
  &__header {
    margin-top: 24px;
    background: $c-white-l;
    padding: 20px;
    border-radius: 4px;

    @media (max-width: 1279px) {
      display: flex;
      flex-direction: column-reverse;

      .main,
      .side {
        width: 100%;
      }

      .side {
        img {
          width: 100%;
        }
      }
    }

    .main {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .side {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &--info {
      display: flex;
			flex-wrap: wrap;
      gap: 6px;
      margin-bottom: 8px;

      .tag {
        background: $c-gray-l;
      }

			::v-deep [data-element="tooltip-text-wrapper"] {
				min-height: rem(20);
				min-width: rem(116);
				margin-inline-start: -64px !important;

				[data-element="tooltip-text"] {
					font-size: 12px !important;
					font-weight: 400 !important;
					line-height: 14px !important;
				}
			}
    }

    &--title {
      @include font(28px, $c-darkest-navy, 500, 36px);
    }

    &--subtitle {
      @include font(18px, $c-darkest-navy, 400, 23px);
    }

    &--description {
      margin-top: 8px;
      display: flex;
      flex-direction: column;
      gap: 6px;
      @include font(16px, $c-darkest-navy, 400, 20px);

      ::v-deep [data-element='introduction'] {
        font-size: rem(18);
        @include rich-text-content;
        @include rich-text-suits;
      }

      &-item {
        display: flex;
        align-items: center;

        .icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 13.5px;
          height: 13.5px;
          margin-right: 5.5px;
        }

        .bold {
          font-weight: 700;
        }
      }
    }

    &--controls {
      display: flex;
      align-items: center;
      margin-top: 8px;
      gap: 16px;

      .btn {
				&:not(.continue-btn) {
					@include font(20px, $c-white-l, 500, 26px);
					padding: 8px 20px;
					gap: 10px;
					border-radius: 4px;
					outline: inherit;
					background: $c-gray-d4;
					border: none;
				}

				&.continue-btn {
					width: fit-content;
				}
      }

      &__price {
        @include font(20px, $c-dark-navy, 500, 26px);

        &.discount {
          text-decoration: line-through;
          opacity: 0.5;
        }

        &.admin {
          @include font(20px, $c-white-l, 500, 26px);
          padding: 8px 20px;
          gap: 10px;
          border-radius: 4px;
          outline: inherit;
          background: $c-dark-navy;
          border: none;
        }
      }

      .progress-wrapper {
        width: 105px;
      }
    }

    &--img {
      width: 277px;
      height: 174px;
      object-fit: cover;
      border-radius: 4px;
    }
  }

  &__body {
    @media (max-width: 1279px) {
      display: flex;
      flex-direction: column;

      .main,
      .side {
        width: 100%;
      }
    }

    &--info {
      display: flex;
      flex-direction: column;

      &-title {
        display: flex;
        align-items: center;
        padding: 7.5px 0;
        margin-bottom: 12px;
        gap: 10px;
        @include font(18px, $c-darkest-navy, 700, 23px);
      }

      &-controlls {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .separator {
          margin: 6px 0;
        }

        .error {
          display: flex;
          gap: 6px;
          justify-content: space-around;
          @include font(14px, $c-medium-navy, 400, 20px);
        }
      }

      &-labels {
        margin-bottom: 36px;
        display: flex;
        flex-wrap: wrap;
        gap: 9px;

        .tag {
          background: $c-gray-l;
        }
      }

      &-teacher {
        display: flex;
        padding: 12px;
        border: 1px solid $c-gray-d2;
        gap: 12px;
        margin-bottom: 36px;
        align-items: center;
        cursor: pointer;

        .teacher {
          &__avatar {
            width: 45px;
            height: 45px;
            object-fit: cover;
            border-radius: 2px;
          }

          &__text {
            display: flex;
            flex-direction: column;
            gap: 4px;

            &--title {
              @include font(18px, $c-dark-navy, 500, 23px);
            }

            &--subtitle {
              @include font(14px, $c-medium-navy, 700, 18px);

              ::v-deep [data-element='introduction'] {
                font-size: rem(18);
                @include rich-text-content;
                @include rich-text-suits;
              }
            }
          }
        }
      }
    }

    &--title {
      @include font(24px, $c-darkest-navy, 500, 31px);
      margin-bottom: 36px;
    }

    &--deals {
      margin-bottom: 36px;

      .create-deal-pack {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-direction: column;
				padding: 40px 36px 20px 36px;
        border: 1px dashed $c-gray-d;
        border-radius: 4px;
        text-align: center;
        @include font(18px, $c-medium-navy, 400, 23px);

        .btn {
          margin-top: 12px;
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 8px 20px;
          border: 1px solid $c-dark-navy;
          border-radius: 4px;
          @include font(16px, $c-dark-navy, 500, 20px);
        }
      }
    }
  }
}

</style>
